'use client';

import React, { useEffect, useState, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import useCountdownTimer from '@/utils/layout/useCountdownTimer';
import useSignalStore from '@/stores/layout/signalStore';
import { usePathname } from 'next/navigation';
import { LayoutProps } from '@/types/generalSettings';
// import useMediaQuery from '@/hooks/useMediaQuery';

/** Alt text for the promo image (the panel gives no alt; an English slug was used before). */
const BANNER_ALT = 'پیشنهاد ویژه خانه سرمایه';

interface TopBarProps {
    topBarData?: LayoutProps.TopBar | null;
    banner?: LayoutProps.BannerItem;
}

const TopBar: React.FC<TopBarProps> = ({ topBarData, banner }) => {
    const { setIsOpen } = useSignalStore();
    const { days, hours, minutes, seconds } = useCountdownTimer(topBarData?.tbTimer);
    // const isDesktop = useMediaQuery('(min-width: 900px)');
    const handleManualOpen = useSignalStore((state) => state.handleManualOpen);
    const hideTransition = useSignalStore((state) => state.hideTransition);
    const pathname = usePathname();

    const [isScrolledUp, setIsScrolledUp] = useState(false); // Track scroll direction
    const lastScrollTop = useRef(0); // a ref: scrolling must not re-render this component on every event

    // Hide TopBar on specific routes
    const hidePaths = ['/start/', '/nokhbegan/', '/trade-webinar-24/', '/best-exchange-landing/'];

    // FIX FOR CLS: Removed `isDesktop` from this condition.
    // This allows the server to render the wrapper HTML instantly, preventing the layout shift.
    // Your CSS class `max-lg:hidden` handles the mobile hiding perfectly on its own.
    const shouldDisplayTopBar = topBarData && !hidePaths.includes(pathname);

    // Scroll event listener to detect scroll direction
    useEffect(() => {
        const handleScroll = () => {
            const currentScrollTop = window.pageYOffset;
            if (currentScrollTop < lastScrollTop.current) {
                // Scrolling up
                setIsScrolledUp(true);
            } else {
                // Scrolling down
                setIsScrolledUp(false);
            }
            lastScrollTop.current = currentScrollTop <= 0 ? 0 : currentScrollTop; // Prevent negative scroll
        };

        if (shouldDisplayTopBar) {
            window.addEventListener('scroll', handleScroll, { passive: true });
        }

        // Cleanup event listener
        return () => window.removeEventListener('scroll', handleScroll);
    }, [shouldDisplayTopBar]); // setIsScrolledUp re-renders only when the direction changes

    // Add a class or style based on scroll direction
    const scrollClass = isScrolledUp ? 'lg:top-0' : 'lg:-top-[64px]'; // Adjust as needed for your layout

    if (!shouldDisplayTopBar) return null;

    return (
        <div
            className={`sticky z-50 h-[64px] transition-all ease-in-out duration-500 hidden lg:block ${scrollClass}`}
        >
            {/* A plain div: the motion wrapper here never animated (initial={false}, no exit
                wrapper) but put the animation library on every page. */}
            <div className={`transition-all ease-in-out duration-500`}>
                {/* Content goes here */}
                {banner?.show ? (
                    banner.type === 'link' ? (
                        <Link
                            href={
                                banner.link?.url ||
                                'https://khanesarmaye.com/landing/earn-dollars/?utm_source=site&utm_medium=header&utm_campaign=newyear'
                            }
                            prefetch={false}
                            target='_blank'
                            rel='noopener'
                            className='shiningTopbar shine-wide relative z-20 block w-full overflow-hidden max-lg:hidden'
                        >
                            <Image
                                src={
                                    banner.image?.sourceUrl ||
                                    '/landing-new-year/desktop-topbar.png'
                                }
                                alt={BANNER_ALT}
                                width={1800}
                                height={64}
                                sizes='100vw'
                                // Lazy, not priority: the bar is display:none on phones, so a lazy
                                // image is never downloaded there (priority preloaded it on every
                                // phone, competing with the real LCP image).
                                loading='lazy'
                                className='max-h-16 object-cover w-full'
                            />
                        </Link>
                    ) : (
                        <button
                            type='button'
                            onClick={() => setIsOpen(true)}
                            aria-haspopup='dialog'
                            className='shiningTopbar shine-wide relative z-20 block w-full cursor-pointer overflow-hidden max-lg:hidden'
                        >
                            <Image
                                src={
                                    banner.image?.sourceUrl ||
                                    '/landing-new-year/desktop-topbar.png'
                                }
                                alt={BANNER_ALT}
                                width={1800}
                                height={64}
                                sizes='100vw'
                                loading='lazy'
                                className='max-h-16 object-cover w-full'
                            />
                        </button>
                    )
                ) : (
                    <div
                        onClick={handleManualOpen}
                        className={`relative cursor-pointer max-h-0 bg-top-bar-gradient-animate shiningTopbar shine-wide z-20 block w-full overflow-hidden text-center text-white transition-all max-xl:hidden ${hideTransition ? 'max-h-0 py-0' : '!max-h-14 !py-1'} ${scrollClass}`}
                    >
                        <div className='absolute inset-x-0 bottom-0 mx-auto scale-150 opacity-[20%]'>
                            <Image
                                src='/lines-1.png'
                                alt=''
                                width={1200}
                                height={180}
                                sizes='100vw'
                                className='mx-auto block size-full'
                                loading='lazy'
                            />
                        </div>
                        <div className='container relative z-10 flex items-center justify-center gap-20'>
                            <span className='text-lg font-light !text-white'>
                                {topBarData.tbMainTitle}
                            </span>
                            <div className='flex items-center gap-5'>
                                <div className='flex flex-row-reverse items-center justify-end gap-1 text-sm 2xl:justify-center'>
                                    <div className='digit flex flex-col rounded-lg px-1 text-white'>
                                        {days}
                                        <span>روز</span>
                                    </div>
                                    <div className='digit flex flex-col rounded-lg px-1 text-white'>
                                        {hours}
                                        <span>ساعت</span>
                                    </div>
                                    <div className='digit flex flex-col rounded-lg px-1 text-white'>
                                        {minutes}
                                        <span>دقیقه</span>
                                    </div>
                                    <div className='digit flex flex-col rounded-lg px-1 text-white'>
                                        {seconds}
                                        <span>ثانیه</span>
                                    </div>
                                </div>
                                <div className='flex justify-end 2xl:justify-center'>
                                    <div className='btn btn-primary rounded-2xl'>
                                        {topBarData.tbBtnText}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TopBar;
